import React, { useMemo, useState, useEffect } from 'react';
import { ZoomIn, X } from 'lucide-react';
import { Button } from '../ui/button';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const normalizeImages = (images) => {
  const toUrl = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'object') {
      return String(v.url || v.src || v.image_url || v.image || '').trim();
    }
    return '';
  };

  const fromArray = (arr) => arr.map(toUrl).filter(Boolean);

  if (!images) return [];

  if (Array.isArray(images)) return fromArray(images);

  if (typeof images === 'string') {
    const s = images.trim();
    if (!s) return [];

    // Handle JSON-encoded arrays: ["\/uploads\/a.jpg", ...]
    if (s.startsWith('[') || s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) return fromArray(parsed);
        if (parsed && typeof parsed === 'object') {
          if (Array.isArray(parsed.images)) return fromArray(parsed.images);
          const u = toUrl(parsed);
          return u ? [u] : [];
        }
      } catch {
        // ignore
      }
    }

    return s.split(',').map((x) => x.trim()).filter(Boolean);
  }

  if (typeof images === 'object') {
    if (Array.isArray(images.images)) return fromArray(images.images);
    const u = toUrl(images);
    return u ? [u] : [];
  }

  return [];
};

const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const cleaned = url.startsWith('/') ? url : `/${url}`;
  return `${BASE_URL}${cleaned}`;
};

const FALLBACK_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"><rect width="800" height="600" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="Arial, sans-serif" font-size="28">Image not available</text></svg>`
)}`;

const ImageZoom = ({ images, productName }) => {
  const imageList = useMemo(() => normalizeImages(images), [images]);
  const [selectedImage, setSelectedImage] = useState(imageList[0] || '');
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (imageList.length && (!selectedImage || !imageList.includes(selectedImage))) {
      setSelectedImage(imageList[0]);
    }
  }, [imageList, selectedImage]);

  const openZoom = () => setIsZoomed(true);
  const closeZoom = () => setIsZoomed(false);

  if (!imageList.length) {
    return (
      <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
        No image
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative aspect-square overflow-hidden rounded-lg bg-gray-100">
        <img
          src={toAbsoluteUrl(selectedImage) || FALLBACK_IMAGE}
          alt={productName}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.src = FALLBACK_IMAGE;
          }}
        />
        <Button
          variant="secondary"
          size="icon"
          className="absolute top-4 right-4"
          onClick={openZoom}
          type="button"
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
      </div>

      {imageList.length > 1 && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {imageList.map((image, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setSelectedImage(image)}
              className={[
                'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2',
                selectedImage === image ? 'border-green-600' : 'border-transparent',
              ].join(' ')}
            >
              <img
                src={toAbsoluteUrl(image) || FALLBACK_IMAGE}
                alt={`${productName} ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = FALLBACK_IMAGE;
                }}
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}

      {isZoomed && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-4 right-4 z-10"
              onClick={closeZoom}
              type="button"
            >
              <X className="h-4 w-4" />
            </Button>
            <img
              src={toAbsoluteUrl(selectedImage) || FALLBACK_IMAGE}
              alt={productName}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.currentTarget.src = FALLBACK_IMAGE;
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageZoom;
